<template>
    <div class="shop-statistics">
        <div class="dashboard-section">
            <div class="dashboard-card">
                <!-- 搜索头部 -->
                <div class="card-header">
                    <div class="search-form">
                        <a-form layout="inline" :model="searchForm" @finish="handleSearch">
                            <div class="form-row">
                                <a-form-item label="店铺数据：" class="form-label">
                                    <div class="date-input-wrapper">
                                        <a-range-picker
                                                v-model:value="searchForm.dateRange"
                                                :max-date="today"
                                                style="width: 220px"
                                                @change="handleDateChange"
                                        />
                                        <a-tooltip>
                                            <template #title>
                                                <div style="font-size: 12px; max-width: 200px;">
                                                    默认时间为当日、当月。日预约订单、日完结订单取日期结束日期。
                                                </div>
                                            </template>
                                            <question-circle-outlined class="help-icon" />
                                        </a-tooltip>
                                    </div>
                                </a-form-item>

                                <a-form-item class="form-label">
                                    <a-select
                                            v-model:value="searchForm.provinceId"
                                            placeholder="选择省"
                                            style="width: 120px"
                                            @change="handleProvinceChange"
                                            :options="provinceOptions"
                                    />
                                </a-form-item>

                                <a-form-item>
                                    <a-select
                                            v-model:value="searchForm.cityId"
                                            placeholder="选择城市"
                                            style="width: 120px"
                                            :options="cityOptions"
                                            :disabled="!searchForm.provinceId"
                                    />
                                </a-form-item>

                                <a-form-item>
                                    <a-input
                                            v-model:value="searchForm.shopName"
                                            placeholder="请输入店铺名称"
                                            style="width: 180px"
                                            allow-clear
                                    />
                                </a-form-item>

                                <a-form-item>
                                    <a-button type="primary" html-type="submit" :loading="loading">
                                        搜索
                                    </a-button>
                                    <a-button
                                            type="default"
                                            @click="handleReset"
                                            style="margin-left: 8px"
                                    >
                                        重置
                                    </a-button>
                                    <a-button
                                            type="link"
                                            @click="handleDownload"
                                            style="margin-left: 8px"
                                    >
                                        下载
                                    </a-button>
                                </a-form-item>
                            </div>
                        </a-form>
                    </div>
                </div>

                <!-- 图表区域 -->
                <div class="chart-container">
                    <div class="chart-wrapper">
                        <!-- 图例标签 -->
                        <div class="chart-labels">
                            <span class="label-left">日统计订单数</span>
                            <span class="label-right">月统计订单数</span>
                        </div>

                        <!-- 图表 -->
                        <div
                                ref="chartRef"
                                class="chart"
                                :style="{ height: chartHeight + 'px' }"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue'
    import { message, Form, Select, Input, Button, DatePicker, Tooltip } from 'ant-design-vue'
    import { QuestionCircleOutlined } from '@ant-design/icons-vue'
    import dayjs from 'dayjs'
    import * as echarts from 'echarts'
    import { dashboardAPI } from '@/services/dashboard'

    const { RangePicker } = DatePicker

    // 状态管理
    const searchForm = ref({
        dateRange: [],
        provinceId: '',
        cityId: '',
        shopName: ''
    })

    const loading = ref(false)
    const chartRef = ref(null)
    const chartInstance = ref(null)
    const chartHeight = ref(1200)
    const provinceOptions = ref([])
    const cityOptions = ref([])
    const today = ref(dayjs())

    // 图表数据
    const chartData = ref({
        shop_name_arr: [],
        day_appointment_count: [],
        month_appointment_count: [],
        day_complete_count: [],
        month_complete_count: []
    })

    // 原始数据备份（用于排序）
    const originalData = ref({})

    // 优化onMounted，确保DOM就绪后再加载数据
    onMounted(async () => {
        // 等待DOM渲染完成
        await nextTick();

        // 初始化图表
        initChart();

        // 延迟加载省份数据，确保响应式数据绑定完成
        setTimeout(async () => {
            await loadProvinces();
            // 加载初始数据
            await loadShopData();
        }, 100);
    })


    const initChart = () => {
        if (!chartRef.value) {
            console.error('图表容器未找到')
            setTimeout(initChart, 100)
            return
        }

        if (chartInstance.value) {
            try {
                chartInstance.value.dispose()
            } catch (e) {
                console.warn('清理图表实例时出错:', e)
            }
            chartInstance.value = null
        }

        try {
            chartInstance.value = echarts.init(chartRef.value)

            // 创建基础配置
            const defaultOption = {
                tooltip: {
                    trigger: 'item',
                    show: true
                },
                legend: {
                    data: ['日预约订单', '月预约订单', '日完结订单', '月完结订单'],
                    top: 10
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    top: '50px',
                    containLabel: true
                },
                xAxis: {
                    type: 'value',
                    name: '订单数量'
                },
                yAxis: {
                    type: 'category',
                    axisTick: {
                        show: false
                    },
                    axisLabel: {
                        fontSize: 11,
                        width: 200,
                        overflow: 'truncate',
                        ellipsis: '...'
                    },
                    data: []
                },
                series: [
                    {
                        name: '日预约订单',
                        type: 'bar',
                        stack: 'appointment',
                        color: '#5470c6',
                        label: {
                            show: true,
                            position: 'left'
                        },
                        data: []
                    },
                    {
                        name: '月预约订单',
                        type: 'bar',
                        stack: 'appointment',
                        color: '#91cc75',
                        label: {
                            show: true,
                            position: 'right'
                        },
                        data: []
                    },
                    {
                        name: '日完结订单',
                        type: 'bar',
                        stack: 'complete',
                        color: '#fc5531',
                        label: {
                            show: true,
                            position: 'left'
                        },
                        data: []
                    },
                    {
                        name: '月完结订单',
                        type: 'bar',
                        stack: 'complete',
                        color: '#fac858',
                        label: {
                            show: true,
                            position: 'right'
                        },
                        data: []
                    }
                ]
            }

            chartInstance.value.setOption(defaultOption)

            console.log('图表初始化完成')

            window.addEventListener('resize', handleResize)

        } catch (error) {
            console.error('图表初始化失败:', error)
        }
    }

    // 加载省份数据
    const loadProvinces = async () => {
        try {
            console.log('开始加载省份数据...');
            const response = await dashboardAPI.getAreaList({ area_parent_id: 0 });

            // 打印完整的响应数据，方便调试
            console.log('省份接口返回数据:', response);

            // 严格检查数据结构
            if (response && response.list && Array.isArray(response.list)) {
                // 转换为Ant Design Select需要的格式
                provinceOptions.value = response.list.map(item => ({
                    label: item.name, // 显示的文本
                    value: item.id.toString() // 确保value是字符串类型，避免类型不匹配
                }));

                console.log('处理后的省份选项:', provinceOptions.value);

                // 验证数据是否正确设置
                if (provinceOptions.value.length === 0) {
                    console.warn('省份数据为空数组');
                    message.warning('未获取到省份数据');
                }
            } else {
                console.error('省份接口返回数据格式不正确', response);
                message.warning('省份数据格式异常，请联系管理员');
                provinceOptions.value = [];
            }
        } catch (error) {
            console.error('加载省份数据失败:', error);
            message.error('加载省份数据失败：' + (error.message || '网络异常'));
            provinceOptions.value = [];
        }
    }

    // 同时优化handleProvinceChange函数，确保城市数据加载也正确
    const handleProvinceChange = async (provinceId) => {
        if (!provinceId) {
            cityOptions.value = [];
            searchForm.value.cityId = '';
            return;
        }

        try {
            console.log('加载城市数据，省份ID:', provinceId);
            const response = await dashboardAPI.getAreaList({ area_parent_id: provinceId });

            console.log('城市接口返回数据:', response);

            if (response && response.list && Array.isArray(response.list)) {
                cityOptions.value = response.list.map(item => ({
                    label: item.name,
                    value: item.id.toString()
                }));
            } else {
                cityOptions.value = [];
                console.warn('城市数据格式不正确');
            }
            searchForm.value.cityId = '';
        } catch (error) {
            console.error('加载城市数据失败:', error);
            message.error('加载城市数据失败');
            cityOptions.value = [];
        }
    }

    // 处理日期变化
    const handleDateChange = (dates) => {
        if (dates && dates.length === 2) {
            console.log('日期范围:', dates.map(date => date.format('YYYY-MM-DD')).join(' ~ '))
        }
    }

    // 处理搜索
    const handleSearch = () => {
        loadShopData()
    }

    // 处理重置
    const handleReset = () => {
        searchForm.value = {
            dateRange: [],
            provinceId: '',
            cityId: '',
            shopName: ''
        }
        cityOptions.value = []
        loadShopData()
    }

    // 处理下载
    const handleDownload = () => {
        const token = localStorage.getItem('token')
        if (!token) {
            message.warning('请先登录')
            return
        }

        const params = new URLSearchParams({
            token,
            province_id: searchForm.value.provinceId || '',
            city_id: searchForm.value.cityId || '',
            date: searchForm.value.dateRange
                ? searchForm.value.dateRange.map(date => date.format('YYYY-MM-DD')).join(' ~ ')
                : '',
            shop_name: searchForm.value.shopName || ''
        }).toString()

        window.open(`/agent/shop_evaluation_order/shopStatisticsExport?${params}`, '_blank')
    }

    // 加载店铺数据
    const loadShopData = async () => {
        if (!chartInstance.value) {
            console.warn('图表未初始化')
            return
        }

        loading.value = true

        try {
            // 准备请求参数
            const params = {
                date: searchForm.value.dateRange
                    ? searchForm.value.dateRange.map(date => date.format('YYYY-MM-DD')).join(' ~ ')
                    : '',
                province_id: searchForm.value.provinceId || '',
                city_id: searchForm.value.cityId || '',
                shop_name: searchForm.value.shopName || ''
            }

            console.log('请求参数:', params)

            const response = await dashboardAPI.getShopData(params)

            if (!response) {
                throw new Error('接口返回数据为空')
            }

            console.log('店铺数据:', response)

            // 处理数据，确保都是数组且长度一致
            const shopNames = Array.isArray(response.shop_name_arr) ? response.shop_name_arr : []
            const dayAppointment = Array.isArray(response.day_appointment_count) ? response.day_appointment_count : []
            const monthAppointment = Array.isArray(response.month_appointment_count) ? response.month_appointment_count : []
            const dayComplete = Array.isArray(response.day_complete_count) ? response.day_complete_count : []
            const monthComplete = Array.isArray(response.month_complete_count) ? response.month_complete_count : []
            const orderShop = Array.isArray(response.business_order_shop) ? response.business_order_shop : []

            // 确保所有数组长度一致（以店铺名称为准）
            const dataLength = shopNames.length

            // 存储数据
            chartData.value = {
                shop_name_arr: shopNames,
                day_appointment_count: ensureArrayLength(dayAppointment, dataLength, 0),
                month_appointment_count: ensureArrayLength(monthAppointment, dataLength, 0),
                day_complete_count: ensureArrayLength(dayComplete, dataLength, 0),
                month_complete_count: ensureArrayLength(monthComplete, dataLength, 0),
                order_shop: ensureArrayLength(orderShop, dataLength, 0),
            }

            // 备份原始数据（用于排序）
            originalData.value = { ...chartData.value }

            // 动态调整图表高度
            chartHeight.value = Math.max(800, dataLength * 35 + 200)

            // 更新图表
            updateChart()

        } catch (error) {
            console.error('加载店铺数据失败:', error)
            message.error('数据加载失败: ' + (error.message || '未知错误'))

            // 即使加载失败，也要设置空数据，避免图表报错
            chartData.value = {
                shop_name_arr: [],
                day_appointment_count: [],
                month_appointment_count: [],
                day_complete_count: [],
                month_complete_count: []
            }
            originalData.value = { ...chartData.value }
            updateChart()
        } finally {
            loading.value = false
        }
    }

    // 确保数组长度一致
    const ensureArrayLength = (arr, length, defaultValue = 0) => {
        if (!Array.isArray(arr)) return new Array(length).fill(defaultValue)

        if (arr.length < length) {
            return [...arr, ...new Array(length - arr.length).fill(defaultValue)]
        } else if (arr.length > length) {
            return arr.slice(0, length)
        }
        return arr
    }

    const updateChart = () => {
        if (!chartInstance.value) return

        const orderShopData = chartData.value.order_shop || []

        const option = {
            tooltip: {
                trigger: 'item',  // 改为 'item' 而不是 'axis'，这样鼠标移到每个柱子上都会显示
                show: true,
                alwaysShowContent: false,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: '#d4d4d4',
                borderWidth: 1,
                textStyle: {
                    color: '#333',
                    fontSize: 12,
                    lineHeight: 18
                },
                padding: [10, 12],
                extraCssText: `
                box-shadow: 0 3px 14px rgba(0,0,0,0.08);
                border-radius: 4px;
                max-width: 300px;
            `,
                formatter: function (params) {

                    // 检查 params 是否有效
                    if (!params || typeof params !== 'object') {
                        return '数据加载中...'
                    }

                    // 如果是数组（trigger: 'axis'），取第一个
                    let param = Array.isArray(params) ? params[0] : params
                    if (!param) return ''

                    let index = param.dataIndex
                    let shopName = param.name || ''

                    // 获取该店铺的所有数据
                    const dayAppointment = chartData.value.day_appointment_count?.[index] || 0
                    const monthAppointment = chartData.value.month_appointment_count?.[index] || 0
                    const dayComplete = chartData.value.day_complete_count?.[index] || 0
                    const monthComplete = chartData.value.month_complete_count?.[index] || 0

                    // 构建 tooltip 内容（按照第二张图的格式）
                    let result = `<div style="font-weight: bold; margin-bottom: 10px; font-size: 14px; color: #333;">${shopName}</div>`

                    // 表格布局
                    result += '<table style="width: 100%; border-collapse: collapse; font-size: 12px;">'

                    // 表头
                    result += '<tr>'
                    result += '<th style="text-align: left; padding: 4px 8px 8px 0; color: #666; font-weight: normal;">项目</th>'
                    result += '<th style="text-align: left; padding: 4px 0 8px 0; color: #666; font-weight: normal;">数量</th>'
                    result += '</tr>'

                    // 添加分割线
                    result += '<tr><td colspan="2" style="padding: 0 0 6px 0; border-bottom: 1px solid #eee;"></td></tr>'

                    // 日预约订单
                    result += '<tr>'
                    result += '<td style="padding: 6px 8px 6px 0;">'
                    result += `<span style="display: inline-block; width: 8px; height: 8px; background-color: #5470c6; border-radius: 2px; margin-right: 6px; vertical-align: middle;"></span>`
                    result += '日预约订单'
                    result += '</td>'
                    result += `<td style="padding: 6px 0; font-weight: bold;">${dayAppointment}</td>`
                    result += '</tr>'

                    // 月预约订单
                    result += '<tr>'
                    result += '<td style="padding: 6px 8px 6px 0;">'
                    result += `<span style="display: inline-block; width: 8px; height: 8px; background-color: #91cc75; border-radius: 2px; margin-right: 6px; vertical-align: middle;"></span>`
                    result += '月预约订单'
                    result += '</td>'
                    result += `<td style="padding: 6px 0; font-weight: bold;">${monthAppointment}</td>`
                    result += '</tr>'

                    // 日完结订单
                    result += '<tr>'
                    result += '<td style="padding: 6px 8px 6px 0;">'
                    result += `<span style="display: inline-block; width: 8px; height: 8px; background-color: #fc5531; border-radius: 2px; margin-right: 6px; vertical-align: middle;"></span>`
                    result += '日完结订单'
                    result += '</td>'
                    result += `<td style="padding: 6px 0; font-weight: bold;">${dayComplete}</td>`
                    result += '</tr>'

                    // 月完结订单
                    result += '<tr>'
                    result += '<td style="padding: 6px 8px 6px 0;">'
                    result += `<span style="display: inline-block; width: 8px; height: 8px; background-color: #fac858; border-radius: 2px; margin-right: 6px; vertical-align: middle;"></span>`
                    result += '月完结订单'
                    result += '</td>'
                    result += `<td style="padding: 6px 0; font-weight: bold;">${monthComplete}</td>`
                    result += '</tr>'

                    // 添加门店统计信息（如果需要）
                    if (orderShopData && orderShopData[index] && Array.isArray(orderShopData[index])) {
                        const shopStats = orderShopData[index]
                        if (shopStats.length > 0) {
                            result += '<tr><td colspan="2" style="padding: 10px 0 6px 0; border-top: 1px solid #eee;"></td></tr>'
                            result += '<tr><td colspan="2" style="padding: 0 0 6px 0; color: #888; font-size: 11px;">月预约门店排名：</td></tr>'

                            shopStats.forEach((shop, i) => {
                                if (shop && shop.username && shop.count !== undefined) {
                                    result += '<tr>'
                                    result += `<td style="padding: 2px 8px 2px 0; color: #666;">${shop.username}</td>`
                                    result += `<td style="padding: 2px 0; color: #666;">${shop.count}</td>`
                                    result += '</tr>'
                                }
                            })
                        }
                    }

                    result += '</table>'
                    return result
                }
            },
            legend: {
                data: ['日预约订单', '月预约订单', '日完结订单', '月完结订单'],
                top: 10,
                textStyle: {
                    fontSize: 12
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '50px',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                name: '订单数量',
                axisLabel: {
                    fontSize: 12
                }
            },
            yAxis: {
                type: 'category',
                axisTick: {
                    show: false
                },
                axisLabel: {
                    fontSize: 11,
                    width: 200,
                    overflow: 'truncate',
                    ellipsis: '...'
                },
                data: chartData.value.shop_name_arr || []
            },
            series: [
                {
                    name: '日预约订单',
                    type: 'bar',
                    stack: 'appointment',
                    color: '#5470c6',
                    label: {
                        show: true,
                        position: 'left',
                        fontSize: 11,
                        formatter: '{c}'
                    },
                    emphasis: {
                        focus: 'self',
                        blurScope: 'coordinateSystem',
                        itemStyle: {
                            shadowColor: 'rgba(0, 0, 0, 0.3)',
                            shadowBlur: 8,
                            shadowOffsetX: 0,
                            shadowOffsetY: 0,
                            opacity: 1
                        }
                    },
                    data: chartData.value.day_appointment_count || []
                },
                {
                    name: '月预约订单',
                    type: 'bar',
                    stack: 'appointment',
                    color: '#91cc75',
                    label: {
                        show: true,
                        fontSize: 11,
                        position: 'right',
                        formatter: '{c}'
                    },
                    emphasis: {
                        focus: 'self',
                        blurScope: 'coordinateSystem',
                        itemStyle: {
                            shadowColor: 'rgba(0, 0, 0, 0.3)',
                            shadowBlur: 8,
                            shadowOffsetX: 0,
                            shadowOffsetY: 0,
                            opacity: 1
                        }
                    },
                    data: chartData.value.month_appointment_count || []
                },
                {
                    name: '日完结订单',
                    type: 'bar',
                    stack: 'complete',
                    color: '#fc5531',
                    label: {
                        show: true,
                        position: 'left',
                        fontSize: 11,
                        formatter: '{c}'
                    },
                    emphasis: {
                        focus: 'self',
                        blurScope: 'coordinateSystem',
                        itemStyle: {
                            shadowColor: 'rgba(0, 0, 0, 0.3)',
                            shadowBlur: 8,
                            shadowOffsetX: 0,
                            shadowOffsetY: 0,
                            opacity: 1
                        }
                    },
                    data: chartData.value.day_complete_count || []
                },
                {
                    name: '月完结订单',
                    type: 'bar',
                    stack: 'complete',
                    color: '#fac858',
                    label: {
                        show: true,
                        fontSize: 11,
                        position: 'right',
                        formatter: '{c}'
                    },
                    emphasis: {
                        focus: 'self',
                        blurScope: 'coordinateSystem',
                        itemStyle: {
                            shadowColor: 'rgba(0, 0, 0, 0.3)',
                            shadowBlur: 8,
                            shadowOffsetX: 0,
                            shadowOffsetY: 0,
                            opacity: 1
                        }
                    },
                    data: chartData.value.month_complete_count || []
                }
            ]
        }

        try {
            console.log('更新图表，数据长度:', {
                店铺数量: chartData.value.shop_name_arr?.length || 0,
                日预约订单: chartData.value.day_appointment_count?.length || 0,
                月预约订单: chartData.value.month_appointment_count?.length || 0
            })

            // 使用 notMerge: true 确保配置完全替换
            chartInstance.value.setOption(option, {
                notMerge: true,  // 关键：完全替换配置
                lazyUpdate: false
            })

            console.log('图表更新成功')

            // 测试：强制显示一次 tooltip
            setTimeout(() => {
                if (chartInstance.value && chartData.value.shop_name_arr?.length > 0) {
                    console.log('强制显示tooltip测试...')
                    chartInstance.value.dispatchAction({
                        type: 'showTip',
                        seriesIndex: 0,
                        dataIndex: 0
                    })

                    setTimeout(() => {
                        chartInstance.value.dispatchAction({
                            type: 'hideTip'
                        })
                    }, 2000)
                }
            }, 800)

        } catch (error) {
            console.error('更新图表失败:', error)
        }
    }

    // 处理窗口大小变化
    let resizeTimer = null
    const handleResize = () => {
        if (chartInstance.value) {
            clearTimeout(resizeTimer)
            resizeTimer = setTimeout(() => {
                chartInstance.value.resize()
            }, 200)
        }
    }

    // 清理
    onUnmounted(() => {
        // 移除事件监听
        window.removeEventListener('resize', handleResize)
        clearTimeout(resizeTimer)

        // 清理图表实例
        if (chartInstance.value) {
            try {
                chartInstance.value.dispose()
            } catch (e) {
                console.warn('清理图表实例时出错:', e)
            }
            chartInstance.value = null
        }
    })
</script>

<style scoped>
    .shop-statistics {
        padding: 8px; /* 减少内边距 */
        background: #f0f2f5;
        min-height: 100%;
        box-sizing: border-box;
        overflow: auto; /* 添加滚动条 */
    }

    .dashboard-section {
        min-height: 105%;
        display: flex;
        flex-direction: column;
    }

    .dashboard-card {
        background: #fff;
        border-radius: 4px;
        box-shadow: 0 1px 2px -2px rgba(0, 0, 0, 0.08),
        0 3px 6px 0 rgba(0, 0, 0, 0.06),
        0 5px 12px 4px rgba(0, 0, 0, 0.04);
        display: flex;
        flex-direction: column;
        min-height: 100%;
    }

    /* 调整搜索头部，使其固定 */
    .card-header {
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;
        flex-shrink: 0; /* 防止搜索区域被压缩 */
        background: #fff;
        position: sticky;
        top: 0;
        z-index: 100;
    }

    /* 图表容器添加滚动 */
    .chart-container {
        flex: 1;
        min-height: 400px; /* 最小高度 */
        overflow-y: auto; /* 垂直滚动 */
        overflow-x: hidden; /* 隐藏水平滚动 */
        padding: 16px;
    }

    .chart-wrapper {
        min-width: 1200px; /* 确保图表有足够宽度 */
    }

    /* 调整图表高度计算逻辑 */
    .chart {
        width: 100%;
        background: #fff;
        min-height: 500px;
    }

    /* 响应式调整 */
    @media (max-width: 1200px) {
        .chart-wrapper {
            min-width: 100%;
        }

        .chart-container {
            padding: 12px;
        }
    }

    @media (max-width: 768px) {
        .shop-statistics {
            padding: 4px;
        }

        .card-header {
            padding: 8px 12px;
        }

        .chart-container {
            padding: 8px;
        }
    }

    .search-form {
        width: 100%;
    }

    .form-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
    }

    .form-label {
        margin-bottom: 0;
    }

    .form-label :deep(.ant-form-item-label) {
        padding: 0;
        line-height: 32px;
    }

    .form-label :deep(.ant-form-item-label label) {
        font-size: 14px;
        color: #333;
    }

    .date-input-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .help-icon {
        color: #999;
        cursor: help;
        font-size: 14px;
    }

    /* 图表容器 */
    .chart-container {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        min-height: 0;
    }

    .chart-wrapper {
        position: relative;
    }

    .chart-labels {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        padding: 0 20px;
    }

    .label-left,
    .label-right {
        font-size: 12px;
        color: #16baaa;
        font-weight: 500;
    }

    .chart {
        width: 100%;
        background: #fff;
        min-height: 500px;
    }

    /* 响应式设计 */
    @media (max-width: 1200px) {
        .form-row {
            gap: 8px;
        }

        .date-input-wrapper {
            width: 100%;
            max-width: 220px;
        }
    }

    @media (max-width: 768px) {
        .shop-statistics {
            padding: 8px;
        }

        .card-header {
            padding: 12px 16px;
        }

        .form-row {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
        }

        .form-label {
            width: 100%;
        }

        .date-input-wrapper {
            width: 100%;
            max-width: none;
        }

        .chart-container {
            padding: 10px;
        }

        .chart-labels {
            padding: 0 10px;
        }
    }

    /* 确保chart容器样式正确 */
    .chart {
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
    }

    /* 修复tooltip被遮挡问题 */
    :deep(.echarts-tooltip) {
        z-index: 99999 !important;
        opacity: 1 !important;
        visibility: visible !important;
        pointer-events: auto !important;
    }

    :deep(.echarts-tooltip > div) {
        opacity: 1 !important;
        visibility: visible !important;
    }

    /* 确保容器不遮挡 */
    .chart-wrapper {
        position: relative;
        overflow: visible !important;
        z-index: 1;
    }

    .chart-container {
        overflow: visible !important;
        position: relative;
        z-index: 1;
    }

    /* 如果有其他可能遮挡的元素 */
    :deep(.ant-card),
    :deep(.ant-card-body) {
        overflow: visible !important;
    }
</style>